import {
  AuthBundleOut,
  CatalogCollectionOut,
  CatalogHomeOut,
  LoginIn,
  PlaceBetIn,
  QuoteIn,
  QuoteOut,
  RegisterIn,
  SessionBootstrapOut,
  SportsCollectionOut,
  TournamentsCollectionOut,
  TicketOut,
  UserOut,
  WalletOut,
  WalletTransactionsOut,
  PaymentIn,
  PaymentOut,
  WithdrawalIn,
  PreferenceOut,
  PreferenceUpdateIn,
  NotificationOut,
  CashoutQuoteIn,
  CashoutQuoteOut,
} from './types';

export const BASE_URL = 'https://135.181.217.145';

function getToken(): string | null {
  return localStorage.getItem('access_token');
}

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onReAuthenticated(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

export function logout(): void {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  window.dispatchEvent(new Event('auth-changed'));
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (res.status === 401 && token && path !== '/v1/auth/refresh') {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshRes = await fetch(`${BASE_URL}/v1/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });

          if (refreshRes.ok) {
            const data: AuthBundleOut = await refreshRes.json();
            localStorage.setItem('access_token', data.tokens.access_token);
            localStorage.setItem('refresh_token', data.tokens.refresh_token);
            onReAuthenticated(data.tokens.access_token);
          } else {
            logout();
            onReAuthenticated('');
          }
        } catch {
          logout();
          onReAuthenticated('');
        } finally {
          isRefreshing = false;
        }
      }

      const newToken = await new Promise<string>((resolve) => {
        refreshSubscribers.push((t) => resolve(t));
      });

      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        res = await fetch(`${BASE_URL}${path}`, { ...options, headers });
      }
    } else {
      logout();
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    let errMsg = `HTTP ${res.status}`;
    if (err?.detail) {
      if (typeof err.detail === 'string') {
        errMsg = err.detail;
      } else if (err.detail.message) {
        errMsg = err.detail.message;
      }
    }
    throw new Error(errMsg);
  }

  return res.json() as Promise<T>;
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function login(data: LoginIn): Promise<AuthBundleOut> {
  const result = await request<AuthBundleOut>('/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  localStorage.setItem('access_token', result.tokens.access_token);
  localStorage.setItem('refresh_token', result.tokens.refresh_token);
  return result;
}

export async function register(data: RegisterIn): Promise<AuthBundleOut> {
  const result = await request<AuthBundleOut>('/v1/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  localStorage.setItem('access_token', result.tokens.access_token);
  localStorage.setItem('refresh_token', result.tokens.refresh_token);
  return result;
}

export async function getMe(): Promise<UserOut> {
  return request<UserOut>('/v1/auth/me');
}

export async function getBootstrap(): Promise<SessionBootstrapOut> {
  return request<SessionBootstrapOut>('/v1/auth/bootstrap');
}

export async function getPreferences(): Promise<PreferenceOut> {
  return request<PreferenceOut>('/v1/auth/preferences');
}

export async function updatePreferences(data: PreferenceUpdateIn): Promise<PreferenceOut> {
  return request<PreferenceOut>('/v1/auth/preferences', {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// ── Catalog ───────────────────────────────────────────────────────────────────

export async function getCatalogHome(params?: {
  live_limit?: number;
  pre_limit?: number;
  sport_id?: string;
  category_id?: string;
  tournament_id?: string;
  country_code?: string;
  pre_starts_after_hours?: number;
  pre_starts_before_hours?: number;
  window_anchor?: string;
  pre_within_hours?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}, options?: RequestInit): Promise<CatalogHomeOut> {
  const qs = new URLSearchParams();
  if (params?.live_limit !== undefined) qs.set('live_limit', String(params.live_limit));
  if (params?.pre_limit !== undefined) qs.set('pre_limit', String(params.pre_limit));
  if (params?.sport_id) qs.set('sport_id', params.sport_id);
  if (params?.category_id) qs.set('category_id', params.category_id);
  if (params?.tournament_id) qs.set('tournament_id', params.tournament_id);
  if (params?.country_code) qs.set('country_code', params.country_code);
  if (params?.pre_starts_after_hours !== undefined) qs.set('pre_starts_after_hours', String(params.pre_starts_after_hours));
  if (params?.pre_starts_before_hours !== undefined) qs.set('pre_starts_before_hours', String(params.pre_starts_before_hours));
  if (params?.window_anchor) qs.set('window_anchor', params.window_anchor);
  if (params?.pre_within_hours !== undefined) qs.set('pre_within_hours', String(params.pre_within_hours));
  if (params?.sort_by) qs.set('sort_by', params.sort_by);
  if (params?.sort_order) qs.set('sort_order', params.sort_order);
  const query = qs.toString() ? `?${qs}` : '';
  return request<CatalogHomeOut>(`/v1/catalog/home${query}`, options);
}

export async function getSports(params?: {
  scope?: string | null;
  within_hours?: number;
  starts_after_hours?: number;
  starts_before_hours?: number;
}, options?: RequestInit): Promise<SportsCollectionOut> {
  const qs = new URLSearchParams();
  if (params?.scope) qs.set('scope', params.scope);
  if (params?.within_hours !== undefined) qs.set('within_hours', String(params.within_hours));
  if (params?.starts_after_hours !== undefined) qs.set('starts_after_hours', String(params.starts_after_hours));
  if (params?.starts_before_hours !== undefined) qs.set('starts_before_hours', String(params.starts_before_hours));
  const query = qs.toString() ? `?${qs}` : '';
  return request<SportsCollectionOut>(`/v1/catalog/sports${query}`, options);
}

export async function getTournaments(params?: {
  scope?: string | null;
  sport_id?: string | null;
  category_id?: string | null;
  country_code?: string | null;
  limit?: number;
  offset?: number;
  sort_by?: 'name' | 'event_count';
  sort_order?: 'asc' | 'desc';
  within_hours?: number;
  starts_after_hours?: number;
  starts_before_hours?: number;
}, options?: RequestInit): Promise<TournamentsCollectionOut> {
  const qs = new URLSearchParams();
  if (params?.scope) qs.set('scope', params.scope);
  if (params?.sport_id) qs.set('sport_id', params.sport_id);
  if (params?.category_id) qs.set('category_id', params.category_id);
  if (params?.country_code) qs.set('country_code', params.country_code);
  if (params?.limit !== undefined) qs.set('limit', String(params.limit));
  if (params?.offset !== undefined) qs.set('offset', String(params.offset));
  if (params?.sort_by) qs.set('sort_by', params.sort_by);
  if (params?.sort_order) qs.set('sort_order', params.sort_order);
  if (params?.within_hours !== undefined) qs.set('within_hours', String(params.within_hours));
  if (params?.starts_after_hours !== undefined) qs.set('starts_after_hours', String(params.starts_after_hours));
  if (params?.starts_before_hours !== undefined) qs.set('starts_before_hours', String(params.starts_before_hours));
  const query = qs.toString() ? `?${qs}` : '';
  return request<TournamentsCollectionOut>(`/v1/catalog/tournaments${query}`, options);
}

export async function getLiveEvents(options?: RequestInit): Promise<CatalogCollectionOut> {
  return request<CatalogCollectionOut>('/v1/catalog/live/events', options);
}

export async function getPreEvents(params?: {
  sport_id?: string;
  tournament_id?: string;
  within_hours?: number;
  starts_after_hours?: number;
  starts_before_hours?: number;
}, options?: RequestInit): Promise<CatalogCollectionOut> {
  const qs = new URLSearchParams();
  if (params?.sport_id) qs.set('sport_id', params.sport_id);
  if (params?.tournament_id) qs.set('tournament_id', params.tournament_id);
  if (params?.within_hours) qs.set('within_hours', String(params.within_hours));
  if (params?.starts_after_hours !== undefined) qs.set('starts_after_hours', String(params.starts_after_hours));
  if (params?.starts_before_hours !== undefined) qs.set('starts_before_hours', String(params.starts_before_hours));
  const query = qs.toString() ? `?${qs}` : '';
  return request<CatalogCollectionOut>(`/v1/catalog/pre/events${query}`, options);
}

export async function getEvent(eventId: string, scope?: string): Promise<any> {
  const query = scope ? `?scope=${encodeURIComponent(scope)}` : '';
  return request(`/v1/catalog/events/${encodeURIComponent(eventId)}${query}`);
}

// ── Bets ──────────────────────────────────────────────────────────────────────

export async function quoteBet(data: QuoteIn): Promise<QuoteOut> {
  return request<QuoteOut>('/v1/bets/quote', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function placeBet(data: PlaceBetIn): Promise<TicketOut> {
  return request<TicketOut>('/v1/bets/place', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}
export async function getBetHistory(limit: number = 50, dateFrom?: string, dateTo?: string, dateField: 'created_at' | 'settled_at' = 'created_at', offset: number = 0, timezone?: string): Promise<TicketOut[]> {
  const params = new URLSearchParams();
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  if (dateFrom) params.append('date_from', dateFrom);
  if (dateTo) params.append('date_to', dateTo);
  params.append('date_field', dateField);
  
  const userTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  params.append('timezone', userTimezone);
  
  return request<TicketOut[]>(`/v1/bets/history?${params.toString()}`, {
    method: 'GET',
  });
}

export async function getBetByReference(reference: string): Promise<TicketOut> {
  return request<TicketOut>(`/v1/bets/by-reference/${encodeURIComponent(reference)}`, {
    method: 'GET',
  });
}

export async function getTicket(ticketId: string): Promise<TicketOut> {
  return request<TicketOut>(`/v1/bets/${encodeURIComponent(ticketId)}`);
}

export async function requestCashoutQuote(ticketId: string): Promise<CashoutQuoteOut> {
  return request<CashoutQuoteOut>('/v1/cashout/quote', {
    method: 'POST',
    body: JSON.stringify({ ticket_id: ticketId } as CashoutQuoteIn),
  });
}

export async function executeCashout(quoteId: string): Promise<TicketOut> {
  return request<TicketOut>(`/v1/cashout/execute/${quoteId}`, {
    method: 'POST',
  });
}

// ── Wallet ────────────────────────────────────────────────────────────────────

export async function getWallet(): Promise<WalletOut> {
  return request<WalletOut>('/v1/wallet');
}

export async function getWalletTransactions(limit: number = 50): Promise<WalletTransactionsOut> {
  return request<WalletTransactionsOut>(`/v1/wallet/transactions?limit=${limit}`);
}

export async function createDeposit(data: PaymentIn): Promise<PaymentOut> {
  return request<PaymentOut>('/v1/wallet/deposits', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createWithdrawal(data: WithdrawalIn): Promise<PaymentOut> {
  return request<PaymentOut>('/v1/wallet/withdrawals', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ── Notifications ─────────────────────────────────────────────────────────────

export async function getNotifications(limit: number = 50): Promise<NotificationOut[]> {
  return request<NotificationOut[]>(`/v1/notifications?limit=${limit}`);
}

export async function markNotificationRead(notificationId: string): Promise<NotificationOut> {
  return request<NotificationOut>(`/v1/notifications/${encodeURIComponent(notificationId)}/read`, {
    method: 'POST',
  });
}
